import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import { getMyUsage, type MyUsage } from "@/services/usageService"

/** useMyUsage の戻り値。 */
interface UseMyUsageResult {
  /** 本人スコープの利用状況。未取得・取得失敗時は null。 */
  usage: MyUsage | null
  /** 利用状況を再取得する。 */
  refresh: () => Promise<void>
}

/**
 * 本人スコープの利用状況（GET /usage/me）をマウント時に取得するフック。
 *
 * エラーは握りつぶし usage を null のままにする（表示を出さない安全側）。
 * 既存レイアウトを壊さないことを優先する。
 * @returns usage（取得結果 or null）と refresh（再取得関数）。
 */
export function useMyUsage(): UseMyUsageResult {
  const { token } = useAuth()
  const [usage, setUsage] = useState<MyUsage | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    if (!token) return
    try {
      const data = await getMyUsage(token)
      setUsage(data)
    } catch {
      // 取得失敗時は表示を出さない（null のまま）。UI を壊さない。
      setUsage(null)
    }
  }, [token])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { usage, refresh }
}
